'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function YuzeDolguYapanDoktorlar() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/yuze-dolgu-yapan-doktorlar-hangi-brans-yetkilidir-nasil-secim-yapilmali';
  const pageTitle = 'Yüze Dolgu Yapan Doktorlar: Hangi Branş Yetkilidir, Nasıl Seçim Yapılmalı?';
  const pageDescription = 'Yüze dolgu işlemini kim yapabilir? Hangi doktorlar yasal olarak dolgu uygulaması yapma yetkisine sahiptir? Uzmanlık farkları, güvenli seçim kriterleri ve klinik uyarılar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Yüze Dolgu Yapan Doktorlar', href: '/tr/blog/yuze-dolgu-yapan-doktorlar-hangi-brans-yetkilidir-nasil-secim-yapilmali' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Yüze dolgu yapan doktorlar ve branş yetkileri"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-violet-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Doktor Seçimi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Yüze Dolgu Yapan Doktorlar: Hangi Branş Yetkilidir, Nasıl Seçim Yapılmalı?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>12 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>15 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-violet-600 text-white rounded-full hover:bg-violet-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-violet-400 text-white rounded-full hover:bg-violet-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-violet-700 text-white rounded-full hover:bg-violet-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Yüz dolgusu, estetik tıbbın en hassas alanlarından biridir.
                Doğal ve dengeli bir yüz ifadesi elde etmek yalnızca doğru ürünle değil, doğru hekimle mümkündür.
              </p>

              <div className="bg-violet-50 border-l-4 border-violet-400 p-6 mb-6">
                <p className="text-violet-800 font-medium text-lg">
                  "Dolgu bir iğne değil, anatomi bilgisidir."
                </p>
                <p className="text-violet-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Yüz Dolgusu Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu, cilt altına hyaluronik asit veya benzeri maddelerin enjekte edilmesiyle yapılan hacim kazandırıcı işlemdir.
                Uygulama sırasında damar, sinir, kas ve kemik yapısının derin bilgisi gerekir.
                Bu nedenle yüz dolgusu yalnızca tıp eğitimi almış ve uygulama yetkisine sahip doktorlar tarafından yapılabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Kimler Dolgu Yapabilir?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-violet-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Branş</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Yasal Yetki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Plastik, Rekonstrüktif ve Estetik Cerrahi Uzmanı</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Yüz anatomi ve doku bilgisi en yüksek branştır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dermatoloji Uzmanı (Cildiye)</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt yapısı ve dolgu davranışı konusunda uzmandır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Medikal Estetik Hekimi (Eğitimli Doktor)</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Tıp fakültesi mezunu olup ek sertifikasyon almış hekimdir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Aile Hekimi veya Diğer Branş Hekimi</td>
                      <td className="border border-gray-300 px-4 py-3 text-yellow-600 font-medium">🔸 Kısmen</td>
                      <td className="border border-gray-300 px-4 py-3">Sadece medikal estetik eğitimi ve sertifikası varsa</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hemşire / Kozmetolog / Estetisyen</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Enjeksiyon yetkisi yoktur</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Sonuç: Yüz dolgusu yalnızca tıp doktorları tarafından yapılabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Yasal Dayanak
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-green-900 mb-4">📜 1219 Sayılı Tababet Kanunu</h4>
                <p className="text-green-800 italic">
                  "Her türlü ilaç ve enjeksiyon uygulaması, yalnızca tıp doktoru veya hekim gözetiminde yapılabilir."
                </p>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-blue-900 mb-4">📜 Sağlık Bakanlığı Yönetmeliği (2024)</h4>
                <p className="text-blue-800 italic">
                  "Dermal dolgu uygulamaları yalnızca hekimler tarafından, onaylı ürünlerle, klinik koşullarda yapılmalıdır."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Hangi Doktor Size Uygun?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Hedef</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Tercih Edilen Branş</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yüz estetiği, hacim kazandırma</td>
                      <td className="border border-gray-300 px-4 py-3">Plastik cerrah veya dermatolog</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dudak dolgusu</td>
                      <td className="border border-gray-300 px-4 py-3">Plastik cerrah veya medikal estetik hekimi</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Göz altı dolgusu</td>
                      <td className="border border-gray-300 px-4 py-3">Plastik cerrah (en hassas bölge)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Burun dolgusu</td>
                      <td className="border border-gray-300 px-4 py-3">Plastik cerrah (anatomi bilgisi kritik)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Eğitim Farkı
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu eğitimi, medikal estetik alanında Tıp Fakültesi mezunu doktorlara özel verilmekte.
                Bu eğitimlerde:
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Yüz anatomisi ve damar yapısı</li>
                  <li>Dolgu maddelerinin özellikleri</li>
                  <li>Enjeksiyon teknikleri</li>
                  <li>Komplikasyon yönetimi</li>
                  <li>Estetik değerlendirme</li>
                </ul>
                <p className="text-yellow-800 mt-4">
                  konularında pratik ve teorik bilgi kazandırılır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Hekim Olmayan Kişiler Neden Riskli?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yanlış enjeksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Asimetri ve doku hasarı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Damar tıkanması</td>
                      <td className="border border-gray-300 px-4 py-3">Doku ölümü ve kalıcı hasar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sterilite eksikliği</td>
                      <td className="border border-gray-300 px-4 py-3">Enfeksiyon</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sahte ürün</td>
                      <td className="border border-gray-300 px-4 py-3">Alerji ve etki kaybı</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  "Dolgu yanlış yere yapmak, estetik değil, anatomik bir hata olur."
                </p>
                <p className="text-red-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Dolgu Uygulayıcısı Seçerken Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Doktor unvanını doğrula (Sağlık Bakanlığı Hekim Bilgi Bankası'ndan)</li>
                  <li>Muayenehane Sağlık Bakanlığı onaylı mı kontrol et</li>
                  <li>Kullanılan dolgu markasını sor (Juvederm, Restylane, Teosyal vb.)</li>
                  <li>Steril ortamda işlem yapıldığından emin ol</li>
                  <li>Onam formu imzalamadan işlem yaptırma</li>
                  <li>Doktorun deneyimini ve sertifikalarını sor</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Uygulamada Fark Yaratan Noktalar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Faktör</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Profesyonel Hekim Yaklaşımı</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Doz ayarı</td>
                      <td className="border border-gray-300 px-4 py-3">Yüz yapısına göre bireysel planlama</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nokta seçimi</td>
                      <td className="border border-gray-300 px-4 py-3">Anatomik varyasyonlara göre belirlenir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Estetik denge</td>
                      <td className="border border-gray-300 px-4 py-3">Yüz ifadeleri korunur</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sonuç takibi</td>
                      <td className="border border-gray-300 px-4 py-3">2 hafta sonra kontrol yapılır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Etik Açıdan Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik tıp, medikal etik kurallara bağlıdır.
                Hekim olmayan birinin dolgu yapması, tıbbi etik ve yasal sorumluluk ihlalidir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  "Estetikte etik, enjeksiyondan daha önemlidir."
                </p>
                <p className="text-purple-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dolgu yapan doktoru nasıl seçmeliyim?</h3>
                  <p className="text-gray-600">Plastik cerrah veya dermatolog tercih edin, deneyimini ve sertifikalarını kontrol edin.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Güzellik merkezinde dolgu güvenli mi?</h3>
                  <p className="text-gray-600">Hayır, yasal değildir. Sadece hekimler dolgu yapabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Dolgu yapan hemşire var mı?</h3>
                  <p className="text-gray-600">Hayır, yalnızca hekim dolgu yapabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Hekimimi nasıl doğrularım?</h3>
                  <p className="text-gray-600">Sağlık Bakanlığı'nın "Hekim Bilgi Bankası" üzerinden sorgulama yapılabilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hangi dolgu markaları güvenli?</h3>
                  <p className="text-gray-600">Juvederm, Restylane, Teosyal, Belotero gibi Sağlık Bakanlığı onaylı markalar güvenlidir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Dolgu işlemi ne kadar sürer?</h3>
                  <p className="text-gray-600">Uzman hekim tarafından yapıldığında 30-60 dakika arası sürer.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Yasal Kaynaklar
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>1219 Sayılı Tababet Kanunu</li>
                  <li>Sağlık Bakanlığı Yönetmeliği, 2024</li>
                  <li>TETD (2024) – "Dermal Dolgu Uygulama Rehberi"</li>
                  <li>ISAPS (2023) – "Qualified Injector Guidelines"</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgusu uygulaması, uzmanlık ve anatomi bilgisi gerektiren ciddi bir tıbbi işlemdir.
                Hekim olmayan kişiler tarafından yapılması, hem yasal hem biyolojik olarak risklidir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Dolgu bir markanın değil, bir doktorun işidir.
                  Kimin eline teslim olduğun, sonucunu belirler."
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#yasaluygulama</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#estetikdoktor</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#sağlıkgüvenliği</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-violet-600 to-purple-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Güvenli Yüz Dolgusu İçin Randevu Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan güvenli yüz dolgusu uygulaması hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Güvenli yüz dolgusu hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-violet-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/botoks-yapan-doktorlar-hangi-brans-yetkilidir-kimler-uygulama-yapabilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Yapan Doktorlar</h4>
              <p className="text-gray-600 text-sm">Hangi branş yetkilidir, kimler uygulama yapabilir?</p>
            </a>
            <a
              href="/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Dolguyu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Sahte ürünlerden nasıl korunulur?</p>
            </a>
            <a
              href="/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Teknikleri</h4>
              <p className="text-gray-600 text-sm">Doğal, simetrik ve estetik sonuçlar.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-violet-600 text-white rounded-full hover:bg-violet-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
